import { Log, LogGroup, Millis, Rect, Size, now } from "../.././MyHelpers";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { ISprite, SpriteUpdateState } from "./ISprite";
import { SpriteSharedData } from "./Sprite";


export interface  PixiFrameSize
{
	w: number;
	h: number;
}

export interface  PixiFrameRect extends PixiFrameSize
{
	x: number;
	y: number;
}

export interface  PixiFrameProps
{
	frame: PixiFrameRect;
	spriteSourceSize: PixiFrameRect;
	sourceSize: PixiFrameSize,
	trimmed: boolean;
	rotated: boolean;
}

export interface  PixiExtendedFrameProps extends PixiFrameProps
{
	frameRect: Rect;
	srcFrameRect: Rect;
}

export interface  PixiFrameByFilename
{
	[key: string]: PixiFrameProps;
}

export interface  PixiSpriteMeta
{
	image: string;
	fps?: number;
}

export interface  PixiSpriteExtendedMeta extends PixiSpriteMeta
{
	fps: number;
}

export interface  PixiSpriteFormat
{
	frames: PixiFrameByFilename;
	meta: PixiSpriteMeta;
}

export interface  PixiExtendedSpriteFormat extends PixiSpriteFormat
{
	framesByIndex: PixiExtendedFrameProps[];
	extendedMeta: PixiSpriteExtendedMeta;
}

/*
class PixiSpriteSharedData
{
	public image: HTMLImageElement = null;
	public decodedFrames: ImageBitmap[] = [];
	public decoded: boolean = false;
}
*/


export class PixiSprite extends ISprite
{

	public image: HTMLImageElement = null;
	public config: PixiExtendedSpriteFormat = null;
	protected frameIndexMap: string[] = null;

	private frameIndex: number = 0;
	private newFrameIndex: number = 0;

	private isPlaying: boolean = false;
	private frameChanged: boolean = false;
	private started: Millis = 0;
	private frameDuration: Millis = 0;

	private logGroup: LogGroup = new LogGroup("Sprite");
	private repeat: boolean = true;

	private resid:ResourceIdentifier = null;

	private size: Size = null;
	private genericRectangle: Rect = null;

	// expected duration based on number of frames and fps, in millis
	private animDuration: number = 0;

	protected shared: SpriteSharedData = new SpriteSharedData();

	//protected resetToFirstFrameOnFinish: boolean = true;
	protected frameResetToStart: boolean = false;

	protected framesToPlay: number = 0;

	protected path: string;

	public get DecodedFrames(): (ImageBitmap | HTMLCanvasElement)[]
	{
		return this.shared.decodedFrames;
	}


	public get CurrentFrame(): ImageBitmap | HTMLCanvasElement
	{
		return this.shared.decodedFrames[this.frameIndex]
	}

	public get CurrentFrameRect(): Rect
	{
		return this.config.framesByIndex[this.frameIndex].frameRect;
	}

	public get CurrentFrameIndex(): number
	{
		return this.frameIndex;
	}

	public override NumFrames(): number {
		return this.shared.decodedFrames.length;
	}
	public override GetDecodedFrame(frameIndex: number): ImageBitmap | HTMLCanvasElement {
		return this.shared.decodedFrames[frameIndex]
	}
	public override GetDecodedFrameRect(frameIndex: number): Rect {
		return this.config.framesByIndex[frameIndex].frameRect;
	}





	public get IdleFrame(): ImageBitmap | HTMLCanvasElement
	{
		return this.shared.decodedFrames[0];
	}

	public get IdleFrameRect(): Rect
	{
		return this.config.framesByIndex[0].frameRect;
	}

	public get IdleFrameIndex(): number
	{
		return 0;
	}


	public get ResourceIdentifier(): ResourceIdentifier
	{
		return this.resid;
	}

	public get LogGroup(): LogGroup
	{
		return this.logGroup;
	}

	public get Duration(): number
	{
		return this.animDuration;
	}

	public ResetToFirstFrame: boolean = true;

	protected static ExtendPixiSpriteFormat(config: PixiSpriteFormat): PixiExtendedSpriteFormat
	{

		let result: PixiExtendedSpriteFormat = {...config} as PixiExtendedSpriteFormat;
		result.framesByIndex = Object.values(result.frames) as PixiExtendedFrameProps[];
		result.framesByIndex.map(f => {
			f.srcFrameRect = new Rect(f.frame.x, f.frame.y, f.frame.w, f.frame.h);
			f.frameRect = new Rect(0, 0, f.frame.w, f.frame.h);
		});
		result.extendedMeta = {...result.meta} as PixiSpriteExtendedMeta;

		if ("fps" in result.meta)
		{
			result.extendedMeta.fps = result.meta.fps;
		}
		else
		{
			result.extendedMeta.fps = 24;
		}

		return result;
	}

	public constructor(image: HTMLImageElement, config: PixiSpriteFormat | PixiExtendedSpriteFormat, path: string, resid: ResourceIdentifier, logGroup: LogGroup, shared: SpriteSharedData = null)
	{
		super();

		this.image = image;

		if ("framesByIndex" in config)
		{
			this.config = {...config};
		}
		else
		{
			this.config = PixiSprite.ExtendPixiSpriteFormat(config);
		}

		this.path = path;
		this.resid = resid;
		this.logGroup = logGroup;

		this.frameDuration = 1000 / this.config.extendedMeta.fps;
		this.animDuration = this.config.framesByIndex.length / this.config.extendedMeta.fps * 1000;
		this.frameIndex = 0; //this.config.framesByIndex.length -1;

		// TODO get from meta? or find from frames?
		this.size = new Size(this.config.framesByIndex[0].frameRect.width, this.config.framesByIndex[0].frameRect.height);
		this.genericRectangle = new Rect(0, 0, this.size.Width, this.size.Height);

		// +1 to return to first frame on the end

		if (shared != null)
		{
			this.shared = shared;
		}
	}

	public Running(): boolean
	{
		return this.isPlaying;
	}

	public Reset(): void 
	{
		this.newFrameIndex = 0;
		//// Log.Debug(this.logGroup, "Reset(): Frame rest to 0");
	}

	public Name():string
	{
		return this.resid.ID();
	}

	public Start(repeat: boolean = true): void 
	{
		this.isPlaying = true;
		this.started = now();

		this.repeat = repeat;
	}

	public Stop(): void 
	{
		this.isPlaying = false;
	}

	public StopAndReset(): void 
	{
		this.Stop();
		this.Reset();
	}

	public Restart(repeat: boolean = true): void 
	{
		this.Reset();
		this.Start(repeat);
	}

	public ToLastFrame(): void 
	{
		this.newFrameIndex = this.config.framesByIndex.length -1;
	}

    public GetFPS(): number
    {
        return Math.roundTo(this.frameDuration / 1000, 2);
    }

	public SetFPS(fps: number): void 
	{
		this.frameDuration = 1000 / fps;
	}

	public Update(millis: number | null): SpriteUpdateState {

		if (this.isPlaying)
		{
			if (millis == null) millis = now();

			let elapsed = millis - this.started;
			this.newFrameIndex = Math.floor(elapsed / this.frameDuration) % this.config.framesByIndex.length;

			//// Log.Debug("SPRITE", "new index " + this.newFrameIndex);
			
			// cap frame index to the last, if animation is not repeated, and stop
			if (!this.repeat && elapsed >= (this.animDuration + this.frameDuration))
			{
				if (this.ResetToFirstFrame)
				{
					this.newFrameIndex = 0;
				}
				else
				{
					this.newFrameIndex = this.config.framesByIndex.length -1;
				}
				
				this.frameResetToStart = false;
				this.isPlaying = false;


				this.OnSpriteComplete();
				return SpriteUpdateState.Finished;
			}
		}

		// notify if frame index changes
		if (this.newFrameIndex != this.frameIndex)
		{

			// need to refresh image!
			this.frameIndex = this.newFrameIndex;
			this.frameChanged = true;

			return SpriteUpdateState.FrameChanged;
		}

		if (!this.isPlaying)
		{
			return SpriteUpdateState.Idle;
		}

		return SpriteUpdateState.NoChange;
	}

	public Repeat(repeat: boolean)
	{
		this.repeat = repeat;
		if (this.repeat == false)
		{
			let n = now();
			let elapsed = n - this.started;
			this.started += Math.floor(elapsed / (this.frameDuration * this.config.framesByIndex.length)) * (this.frameDuration * this.config.framesByIndex.length) ;
		}
	}

	// public CurrentFrameRect(): PixelRect {
	// 	return this.config.framesByIndex[this.frameIndex].frameRect;
	// }

	// returns rect of given frame index
	public RectOfFrame(frameIndex: number): Rect {

		let rect: Rect = null;
		if (frameIndex >= 0 && frameIndex < this.config.framesByIndex.length) {
			rect = this.config.framesByIndex[frameIndex].frameRect;
		}
		else {
			Log.Error(this.logGroup, "Frame index " + frameIndex + " does not exist")
		}

		return rect;
	}

	public get GenericRectangle()
	{
		return this.genericRectangle;
	}

	// checks and resets frame changed flag
	public FrameChanged(): boolean {
		let result: boolean = false;

		if (this.isPlaying || !this.frameResetToStart) {
			result = this.frameChanged;
		}
		
		this.frameResetToStart = true;
		this.frameChanged = false;
		
		return result;
	}

	// public CurrentFrameIndex(): number {
	// 	return this.frameIndex;
	// }

	public Size(): Size {
		return this.size
		;
	}

	// shallow copy, keep the same instance of image and json config
	public Copy(): ISprite {
		//return new PixiSprite(this.image.cloneNode() as HTMLImageElement, this.config, )
		return new PixiSprite(this.image.cloneNode() as HTMLImageElement, this.config, this.path, this.resid, this.logGroup, this.shared);
	}




	protected numDecodedFrames: number = 0;
	protected numErrorFrames: number = 0;
	protected numProcessedFrames: number = 0;

	protected decodeStartTime: number = 0;

	protected decodedFramesSize: number = 0;

	public Decode(): void
{
	if (this.shared.decodedFrames.length !== 0 || this.shared.decoded)
	{
		Log.Error(this.logGroup, "Frames already decoded, skipping...");
		debugger;
		return;
	}

	this.shared.decoded = true;
	this.decodeStartTime = now();

	const twoStepDecoding = true;

	if ("createImageBitmap" in globalThis)
	{
		if (twoStepDecoding)
		{
			createImageBitmap(this.image).then((decodedImage: ImageBitmap) => {
				let decodeWholeImage = now();

				this.config.framesByIndex.forEach((frameProps: PixiExtendedFrameProps, index: number) => {
					createImageBitmap(
						decodedImage,
						frameProps.srcFrameRect.x,
						frameProps.srcFrameRect.y,
						frameProps.srcFrameRect.width,
						frameProps.srcFrameRect.height
					).then((decodedFrame: ImageBitmap) => {
						this.shared.decodedFrames[index] = decodedFrame;
						this.FrameProcessed(frameProps, index, true);
					}).catch((reason: any) => {
						this.FrameProcessed(frameProps, index, false);
					});
				});
			}).catch((reason: any) => {
				Log.Error(this.logGroup, "Decoding whole image failed");
				debugger;
			});
		}
		else
		{
			this.config.framesByIndex.forEach((frameProps: PixiExtendedFrameProps, index: number) => {
				createImageBitmap(
					this.image,
					frameProps.srcFrameRect.x,
					frameProps.srcFrameRect.y,
					frameProps.srcFrameRect.width,
					frameProps.srcFrameRect.height
				).then((decodedFrame: ImageBitmap) => {
					this.shared.decodedFrames[index] = decodedFrame;
					this.FrameProcessed(frameProps, index, true);
				}).catch((reason: any) => {
					this.FrameProcessed(frameProps, index, false);
				});
			});
		}
	}
	else
	{
		// ❗ createImageBitmap not supported: use canvas fallback
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');

		if (!ctx) {
			Log.Critical(this.logGroup, "Canvas context unavailable");
			this.config.framesByIndex.forEach((frameProps, index) =>
				this.FrameProcessed(frameProps, index, false)
			);
			return;
		}

		canvas.width = this.image.width;
		canvas.height = this.image.height;
		ctx.drawImage(this.image, 0, 0);

		this.config.framesByIndex.forEach((frameProps: PixiExtendedFrameProps, index: number) => {
			try {
				const frameCanvas = document.createElement('canvas');
				frameCanvas.width = frameProps.srcFrameRect.width;
				frameCanvas.height = frameProps.srcFrameRect.height;

				const frameCtx = frameCanvas.getContext('2d');
				if (!frameCtx) throw new Error("No 2D context");

				frameCtx.drawImage(
					canvas,
					frameProps.srcFrameRect.x,
					frameProps.srcFrameRect.y,
					frameProps.srcFrameRect.width,
					frameProps.srcFrameRect.height,
					0, 0,
					frameProps.srcFrameRect.width,
					frameProps.srcFrameRect.height
				);

				this.shared.decodedFrames[index] = frameCanvas;
				this.FrameProcessed(frameProps, index, true);
			}
			catch (e)
			{
				this.FrameProcessed(frameProps, index, false);
			}
		});
	}
}


	protected FrameProcessed(frameProps: PixiExtendedFrameProps, frameIndex: number, success: boolean)
	{


		this.decodedFramesSize += this.config.framesByIndex[frameIndex].frameRect.width * this.config.framesByIndex[frameIndex].frameRect.height * 4;

		if (success)
		{
			this.numDecodedFrames++;
		}
		else
		{
			this.numErrorFrames++;
		}

		this.numProcessedFrames++;
		if (this.numProcessedFrames == this.config.framesByIndex.length)
		{
			let decodeEndTime = now();

			//// Log.Debug(this.logGroup, "Decode took " + ((decodeEndTime - this.decodeStartTime) / 1000).toFixed(2) + "s");
			//// Log.Debug(this.logGroup, "Decoded sprite size: " + formatBytes(this.decodedFramesSize) + " (" + this.path + ")");

			if (this.numErrorFrames == 0)
			{
				//// Log.Debug(this.logGroup, "Processed all " + this.config.frames.length + " frames, finished decoding");
				this.FramesDecoded();
			}
			else
			{
				//// Log.Debug(this.logGroup, "Processed all frames, error decoding");
				this.FramesDecodeError();
			}
		}
	}

	protected FrameDecoded(decodedFrame: ImageBitmap, frameIndex: number)
	{
		//// Log.Debug(this.logGroup, "Decoded frame " + frameIndex);

		this.numDecodedFrames++;
		if (this.numDecodedFrames == this.config.framesByIndex.length)
		{
			//// Log.Debug(this.logGroup, "Decoded all frames, finished decoding");
			this.FramesDecoded();
		}
	}

	public Clean(): void
	{
		this.numProcessedFrames = 0;
		this.numDecodedFrames = 0;
		this.numErrorFrames = 0;

		this.shared.decodedFrames = [];

		// Log.Debug(this.logGroup, "Cleanup finished");
		this.FramesCleaned();
	}
}
